import React, { useState } from "react";
import Box from "react-raster";
import styled from "styled-components";
import { useInView } from "react-intersection-observer";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import Container from "../Container";
import { H2, Eyebrow } from "../Typography";
import Video from "../Video";
import Arrow from "../Arrow";
import PlayButton from "../Video/PlayButton";

const IntersectingTitle = styled(H2)`
  color: ${({ isIntersecting }) => isIntersecting && "white"};
  transition: color 0.5s ease;
  margin-bottom: 120px;

  @media (${({ theme }) => theme.mq.m}) {
    margin-bottom: 60px;
  }
`;

const StickyBox = styled(Box)`
  position: sticky;
  top: 70vh;
  z-index: 10;
  padding: 0 ${({ theme }) => theme.containerPadding[0]};

  // workaround to prevent orphaned last word with current copy
  @media (min-width: 1615px) and (max-width: 1715px) {
    max-width: 1030px;
  }
`;

const FullWidthWrapper = styled.div`
  background-color: ${({ theme, $backgroundColour }) =>
    theme.colours[$backgroundColour]};
  width: 100%;
  padding-bottom: ${({ $hasBottomMargin }) =>
    $hasBottomMargin ? "60px" : "20px"};
  padding-top: ${({ $hasTopMargin }) => ($hasTopMargin ? "60px" : "20px")};
`;

function StickyTitleWithImage({
  eyebrow,
  title,
  image,
  alignment,
  backgroundColour,
  video,
  hideTitle,
  anchorId,
  hasBottomMargin,
  hasTopMargin,
}) {
  const initialVideoState =
    typeof window !== "undefined" && window.location.hash === `#${anchorId}`;
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(initialVideoState);
  const left = {
    left: 0,
    centre: 3,
    right: 6,
  };
  const { ref, inView } = useInView({
    threshold: 0.2,
  });

  return (
    <FullWidthWrapper
      $backgroundColour={backgroundColour}
      $hasBottomMargin={hasBottomMargin}
      $hasTopMargin={hasTopMargin}
    >
      <Container
        isFluid
        marginBottom={0}
        styles={`
        align-self: flex-start; // allows stickiness
        margin-bottom: 100px; // prevents background colour bleed
      `}
      >
        <StickyBox
          id={anchorId}
          marginTop={[0, 1]}
          cols={[6, 12, alignment === "right" ? 6 : 8]}
          marginBottom={1}
          marginLeft={[0, 0, left[alignment] || 0]}
        >
          <>
            {eyebrow && <Eyebrow colour="blue">{eyebrow}</Eyebrow>}
            {!hideTitle && (
              <IntersectingTitle
                isIntersecting={
                  backgroundColour === "blue" ||
                  backgroundColour === "blue" ||
                  inView
                }
              >
                {title}
              </IntersectingTitle>
            )}
            {video?.file && (
              <PlayButton onClick={() => setIsVideoModalOpen(true)} />
            )}
          </>
          {!video?.file && <Arrow colour="white" isLong />}
        </StickyBox>
        <Box cols={[6, 12]}>
          <div ref={ref}>
            {video?.file && (
              <Video
                video={video.file.url}
                poster={image.fluid?.src}
                isOpen={isVideoModalOpen}
                closeModal={() => setIsVideoModalOpen(false)}
              />
            )}
            {image && <GatsbyImage image={getImage(image)} />}
          </div>
        </Box>
      </Container>
    </FullWidthWrapper>
  );
}

export default StickyTitleWithImage;
